import { useState, useEffect } from "react";
import axios from "axios";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoAddOutline, IoCloseOutline } from "react-icons/io5";


const BASE_URL = "https://staging.pteshstudios.com";

const emptyForm = { content: "", name: "", position: "" };

export default function AdminTestimonial() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  // ── GET all ──
  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/api/testimonials`);
      console.log("Testimonials response:", data);
      const payload = data?.data ?? data;
      const list = Array.isArray(payload) ? payload : [];
      setTestimonials(list);
    } catch (err) {
      console.error("Failed to fetch testimonials:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  // ── Validate ──
  const validate = (data) => {
    const errs = {};
    if (!data.name.trim()) errs.name = "Name is required";
    if (!data.position.trim()) errs.position = "Position is required";
    if (!data.content.trim()) errs.content = "Testimonial text is required";
    return errs;
  };

  // ── POST — add ──
  const openAdd = () => {
    setForm(emptyForm);
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleAdd = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) return setFormErrors(errs);
    setAdding(true);
    try {
      await axios.post(`${BASE_URL}/api/testimonials`, form);
      setShowAddModal(false);
      fetchTestimonials();
    } catch (err) {
      console.error("Failed to add testimonial:", err);
    } finally {
      setAdding(false);
    }
  };

  // ── PUT — update ──
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ content: item.content, name: item.name, position: item.position });
    setFormErrors({});
  };

  const handleUpdate = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) return setFormErrors(errs);
    setSaving(true);
    try {
      await axios.put(`${BASE_URL}/api/testimonials/${editItem._id}`, form);
      setEditItem(null);
      fetchTestimonials();
    } catch (err) {
      console.error("Failed to update testimonial:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE ──
  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${BASE_URL}/api/testimonials/${id}`);
      setDeleteId(null);
      fetchTestimonials();
    } catch (err) {
      console.error("Failed to delete testimonial:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  return (
    <>
      <style>{styles}</style>
      <div className="at__page">

        {/* ── Header ── */}
        <div className="at__header">
          <div>
            <h1 className="at__page-title">Testimonials</h1>
            <p className="at__page-sub">{testimonials.length} testimonial{testimonials.length !== 1 ? "s" : ""}</p>
          </div>
          <button className="at__add-btn" onClick={openAdd}>
            <IoAddOutline size={18} />
            Add Testimonial
          </button>
        </div>

        {/* ── Table ── */}
        <div className="at__table-wrap">
          {loading ? (
            <p style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>Loading testimonials...</p>
          ) : (
            <table className="at__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Person</th>
                  <th>Testimonial</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.length === 0 && (
                  <tr>
                    <td colSpan={4} className="at__empty">No testimonials yet. Add one above.</td>
                  </tr>
                )}
                {testimonials.map((t, i) => (
                  <tr key={t._id} className="at__row">
                    <td className="at__td-num">{i + 1}</td>
                    <td className="at__td-person">
                      <div>
                        <p className="at__name">{t.name}</p>
                        <p className="at__role">{t.position}</p>
                      </div>
                    </td>
                    <td className="at__td-text">
                      <p className="at__quote">"{t.content}"</p>
                    </td>
                    <td className="at__td-actions">
                      <button className="at__icon-btn at__icon-btn--edit" title="Edit" onClick={() => openEdit(t)}>
                        <CiEdit size={17} />
                      </button>
                      <button className="at__icon-btn at__icon-btn--delete" title="Delete" onClick={() => setDeleteId(t._id)}>
                        <RiDeleteBin6Line size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Add Modal ── */}
        {showAddModal && (
          <Modal title="Add Testimonial" onClose={() => setShowAddModal(false)}>
            <TestimonialForm form={form} errors={formErrors} onChange={handleFormChange} />
            <ModalActions
              onCancel={() => setShowAddModal(false)}
              onConfirm={handleAdd}
              confirmLabel={adding ? "Adding..." : "Add"}
              confirmClass="at__modal-btn--save"
              disabled={adding}
            />
          </Modal>
        )}

        {/* ── Edit Modal ── */}
        {editItem && (
          <Modal title="Edit Testimonial" onClose={() => setEditItem(null)}>
            <TestimonialForm form={form} errors={formErrors} onChange={handleFormChange} />
            <ModalActions
              onCancel={() => setEditItem(null)}
              onConfirm={handleUpdate}
              confirmLabel={saving ? "Saving..." : "Save Changes"}
              confirmClass="at__modal-btn--save"
              disabled={saving}
            />
          </Modal>
        )}

        {/* ── Delete Confirm ── */}
        {deleteId && (
          <Modal title="Delete Testimonial?" onClose={() => setDeleteId(null)} small>
            <p className="at__modal-body">This testimonial will be permanently removed. This action cannot be undone.</p>
            <ModalActions
              onCancel={() => setDeleteId(null)}
              onConfirm={() => handleDelete(deleteId)}
              confirmLabel={deleting ? "Deleting..." : "Delete"}
              confirmClass="at__modal-btn--delete"
              disabled={deleting}
            />
          </Modal>
        )}
      </div>
    </>
  );
}

// ── Reusable sub-components ──
function Modal({ title, onClose, children, small }) {
  return (
    <div className="at__overlay" onClick={onClose}>
      <div className={`at__modal${small ? " at__modal--sm" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="at__modal-head">
          <h3 className="at__modal-title">{title}</h3>
          <button className="at__modal-close" onClick={onClose}><IoCloseOutline size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TestimonialForm({ form, errors, onChange }) {
  return (
    <div className="at__form">
      <div className="at__form-row">
        <div className="at__form-group">
          <label className="at__label">Full Name</label>
          <input
            className={`at__input${errors.name ? " at__input--err" : ""}`}
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="e.g. Hans Müller"
          />
          {errors.name && <span className="at__err">{errors.name}</span>}
        </div>
        <div className="at__form-group">
          <label className="at__label">Position & Company</label>
          <input
            className={`at__input${errors.position ? " at__input--err" : ""}`}
            name="position"
            value={form.position}
            onChange={onChange}
            placeholder="e.g. Snr Manager, Grandiox"
          />
          {errors.position && <span className="at__err">{errors.position}</span>}
        </div>
      </div>
      <div className="at__form-group">
        <label className="at__label">Testimonial</label>
        <textarea
          className={`at__input at__textarea${errors.content ? " at__input--err" : ""}`}
          name="content"
          value={form.content}
          onChange={onChange}
          placeholder="Write the testimonial here..."
          rows={4}
        />
        {errors.content && <span className="at__err">{errors.content}</span>}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, onConfirm, confirmLabel, confirmClass, disabled }) {
  return (
    <div className="at__modal-actions">
      <button className="at__modal-btn at__modal-btn--cancel" onClick={onCancel}>Cancel</button>
      <button className={`at__modal-btn ${confirmClass}`} onClick={onConfirm} disabled={disabled}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
        {confirmLabel}
      </button>
    </div>
  );
}

const styles = `
  .at__page {
    padding: 32px;
    background: #f8f9fb;
    min-height: 100vh;
    font-family: inherit;
    box-sizing: border-box;
  }
  .at__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .at__page-title { font-size: 24px; font-weight: 700; color: #111827; margin: 0 0 4px; letter-spacing: -0.3px; }
  .at__page-sub { font-size: 13px; color: #9ca3af; margin: 0; }
  .at__add-btn {
    display: flex; align-items: center; gap: 7px; background: #1d4ed8; color: #fff;
    border: none; border-radius: 8px; padding: 10px 20px; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: background 0.2s; white-space: nowrap;
  }
  .at__add-btn:hover { background: #1e40af; }
  .at__table-wrap {
    background: #fff; border-radius: 14px; border: 1px solid #e5e7eb;
    overflow: hidden; box-shadow: 0 1px 6px rgba(0,0,0,0.05);
  }
  .at__table { width: 100%; border-collapse: collapse; }
  .at__table thead tr { background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
  .at__table th {
    text-align: left; padding: 13px 18px; font-size: 12px; font-weight: 600;
    color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em;
  }
  .at__row { border-bottom: 1px solid #f3f4f6; transition: background 0.15s; }
  .at__row:last-child { border-bottom: none; }
  .at__row:hover { background: #fafafa; }
  .at__table td { padding: 14px 18px; vertical-align: top; }
  .at__td-num { color: #9ca3af; font-size: 13px; width: 40px; }
  .at__td-person { display: flex; align-items: center; gap: 12px; min-width: 180px; }
  .at__name { font-size: 14px; font-weight: 600; color: #111827; margin: 0 0 2px; white-space: nowrap; }
  .at__role { font-size: 12px; color: #6b7280; margin: 0; white-space: nowrap; }
  .at__td-text { max-width: 420px; }
  .at__quote {
    font-size: 13px; color: #4b5563; line-height: 1.6; margin: 0;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .at__td-actions { display: flex; gap: 8px; align-items: center; }
  .at__icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 7px; border: 1.5px solid transparent;
    cursor: pointer; transition: all 0.18s; background: transparent;
  }
  .at__icon-btn--edit { border-color: #d1d5db; color: #374151; background: #f9fafb; }
  .at__icon-btn--edit:hover { background: #e0e7ff; border-color: #6366f1; color: #4f46e5; }
  .at__icon-btn--delete { border-color: #fecaca; color: #ef4444; background: #fff5f5; }
  .at__icon-btn--delete:hover { background: #fee2e2; border-color: #ef4444; }
  .at__empty { text-align: center; color: #9ca3af; font-size: 14px; padding: 40px !important; }
  .at__overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .at__modal {
    background: #fff; border-radius: 14px; width: 100%; max-width: 540px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden;
  }
  .at__modal--sm { max-width: 380px; }
  .at__modal-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 24px 16px; border-bottom: 1px solid #f3f4f6;
  }
  .at__modal-title { font-size: 17px; font-weight: 700; color: #111827; margin: 0; }
  .at__modal-close { background: none; border: none; color: #9ca3af; cursor: pointer; display: flex; padding: 4px; border-radius: 6px; transition: color 0.2s; }
  .at__modal-close:hover { color: #374151; }
  .at__modal-body { padding: 16px 24px; font-size: 14px; color: #6b7280; line-height: 1.6; }
  .at__modal-actions { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px 20px; }
  .at__modal-btn { padding: 9px 22px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: background 0.2s; }
  .at__modal-btn--cancel { background: #f3f4f6; color: #374151; }
  .at__modal-btn--cancel:hover { background: #e5e7eb; }
  .at__modal-btn--save { background: #1d4ed8; color: #fff; }
  .at__modal-btn--save:hover { background: #1e40af; }
  .at__modal-btn--delete { background: #ef4444; color: #fff; }
  .at__modal-btn--delete:hover { background: #dc2626; }
  .at__form { display: flex; flex-direction: column; gap: 14px; padding: 20px 24px 8px; }
  .at__form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  @media (max-width: 500px) {
    .at__form-row { grid-template-columns: 1fr; }
    .at__page { padding: 16px; }
    .at__td-person { min-width: unset; }
  }
  .at__form-group { display: flex; flex-direction: column; gap: 5px; }
  .at__label { font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
  .at__input {
    border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 9px 12px; font-size: 14px;
    font-family: inherit; outline: none; transition: border-color 0.2s; color: #111827;
    background: #fff; width: 100%; box-sizing: border-box;
  }
  .at__input:focus { border-color: #1d4ed8; }
  .at__input--err { border-color: #ef4444 !important; }
  .at__textarea { resize: vertical; }
  .at__err { font-size: 11px; color: #ef4444; font-weight: 500; }
`;